/**
 * User Profile Component
 * =============================================================================
 * Displays user info and provides logout functionality
 *
 * Features:
 * - Avatar trigger opening a disclosure popover (name, email, links, sign out)
 * - Escape / outside click close it
 * - Mobile responsive
 * - Loading and error states
 */

import React, { useContext, useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { ShellContext } from '../shell/shellContext';
import CacheManagerModal from './CacheManagerModal';

interface UserProfileProps {
  showRelevanceScore?: boolean;
  onRelevanceScoreChange?: (value: boolean) => void;
}

const UserProfile: React.FC<UserProfileProps> = ({
  showRelevanceScore: showRelevanceScoreProp = false,
  onRelevanceScoreChange: onRelevanceScoreChangeProp,
}) => {
  // Inside the UI shell the toggle is shared by every tab; elsewhere the props apply.
  const shell = useContext(ShellContext);
  const showRelevanceScore = shell ? shell.showRelevanceScore : showRelevanceScoreProp;
  const onRelevanceScoreChange = shell ? shell.setShowRelevanceScore : onRelevanceScoreChangeProp;
  const { user, signOut, loading } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isCacheModalOpen, setIsCacheModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // Check if current user is an admin (DB-backed — see useIsAdmin.ts)
  const isAdmin = useIsAdmin();

  // Close on outside click, and on Escape (focus returns to the trigger)
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      console.log('✅ Logout successful');
      navigate('/login');
    } catch (error) {
      console.error('❌ Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
      setIsDropdownOpen(false);
    }
  };

  // Don't render if no user or still loading
  if (loading || !user) {
    return null;
  }

  // Get user initials for avatar
  const getInitials = (name: string | null, email: string | null): string => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        const firstChar = parts[0][0];
        const secondChar = parts[1][0];
        if (firstChar && secondChar) {
          return (firstChar + secondChar).toUpperCase();
        }
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const displayName = user.displayName || user.email?.split('@')[0] || 'User';
  const initials = getInitials(user.displayName, user.email);

  const itemCls =
    'w-full min-h-[40px] px-2.5 rounded-ctl text-left text-sm text-ink hover:bg-ground focus-visible:bg-ground transition-colors flex items-center';
  const go = (path: string) => {
    setIsDropdownOpen(false);
    navigate(path);
  };
  const avatarCls = 'rounded-full bg-ink text-ground flex items-center justify-center font-medium overflow-hidden';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`${avatarCls} w-[34px] h-[34px] text-xs border-2 border-accent`}
        aria-label="User menu"
        aria-expanded={isDropdownOpen}
        aria-controls="account-menu-popover"
      >
        {user.photoURL ? (
          <img src={user.photoURL} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          <span>{initials}</span>
        )}
      </button>

      {isDropdownOpen && (
        <div
          id="account-menu-popover"
          className="absolute right-0 mt-2 w-[290px] bg-white rounded border border-line-input shadow-[0_12px_32px_rgba(19,28,25,0.14)] z-50 overflow-hidden"
        >
          <div className="p-4 flex items-center gap-3 border-b border-line-subtle">
            <span aria-hidden="true" className={`${avatarCls} w-10 h-10 shrink-0`}>{initials}</span>
            <div className="min-w-0 flex flex-col gap-0.5">
              <span className="font-semibold text-ink truncate">{displayName}</span>
              <span className="text-xs text-ink-2 truncate">{user.email}</span>
            </div>
          </div>

          <div className="p-1.5 flex flex-col">
            <button type="button" onClick={() => go('/account')} className={itemCls}>
              Your account
            </button>
            <button type="button" onClick={() => go('/manage-drafts')} className={itemCls}>
              Manage Drafts
            </button>
          </div>

          {isAdmin && (
            <div className="p-1.5 border-t border-line-subtle flex flex-col">
              <button type="button" onClick={() => go('/admin')} className={itemCls}>
                Admin Dashboard
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsCacheModalOpen(true);
                  setIsDropdownOpen(false);
                }}
                className={itemCls}
              >
                Manage Cache
              </button>
              <label className="min-h-[40px] px-2.5 flex items-center justify-between cursor-pointer">
                <span className="text-sm text-ink">Show Relevance Score</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showRelevanceScore}
                  onClick={() => onRelevanceScoreChange?.(!showRelevanceScore)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    showRelevanceScore ? 'bg-accent' : 'bg-line-input'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                      showRelevanceScore ? 'translate-x-[18px]' : 'translate-x-[3px]'
                    }`}
                  />
                </button>
              </label>
            </div>
          )}

          <div className="p-1.5 border-t border-line-subtle flex flex-col">
            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={`${itemCls} !text-ink-2 disabled:opacity-50`}
            >
              {isLoggingOut ? 'Signing out...' : 'Sign out'}
            </button>
          </div>
        </div>
      )}
      <CacheManagerModal
        isOpen={isCacheModalOpen}
        onClose={() => setIsCacheModalOpen(false)}
      />
    </div>
  );
};

export default UserProfile;
